<template>
  <v-container fluid>
    <div class="main-title-bar">
        <TitleBar title="Matriz SWOT" url="/swot/menu" />
    </div>

    <v-row class="mt-2">
      <!-- Coluna da Árvore de Setores -->
      <v-col cols="3" v-show="showSidebar">
        <SectorTreeList v-if="sector.id" :sectorId="sector.id" @selected="setSector" />
      </v-col>

      <!-- Coluna da Matriz SWOT -->
      <v-col :cols="showSidebar ? 9 : 12">
        <v-toolbar flat dense class="mb-2">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" @click="showSidebar = !showSidebar">
                <v-icon>{{ showSidebar ? 'mdi-chevron-double-left' : 'mdi-chevron-double-right' }}</v-icon>
              </v-btn>
            </template>
            <span>{{ showSidebar ? 'Ocultar' : 'Exibir' }} árvore de setores</span>
          </v-tooltip>
          <template v-if="sector.id">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-toolbar-title
                  v-bind="attrs"
                  v-on="on"
                  class="subtitle-1 sector-name-btn"
                  @click="showSidebar = !showSidebar"
                >
                  <v-icon small color="primary" class="mr-1">mdi-sitemap</v-icon>
                  {{ sector.name }}
                  <v-icon small color="primary" class="ml-1">{{ showSidebar ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-toolbar-title>
              </template>
              <span>Clique para {{ showSidebar ? 'ocultar' : 'exibir' }} a árvore de setores</span>
            </v-tooltip>
          </template>
          <v-toolbar-title v-else class="subtitle-1">
            Selecione um Setor
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn v-if="!showCrossButtons" class="my-2 mr-2" color="primary" dense dark @click="toggleCrossButtons" :disabled="!sector.id">Nova Iniciativa</v-btn>
          <v-btn v-if="showCrossButtons && hasSelection" class="my-2" color="success" dense dark @click="createInitiative">Criar Iniciativa</v-btn>
        </v-toolbar>

        <div v-if="!sector.id" class="text-center grey--text mt-10">
          <v-icon large>mdi-sitemap</v-icon>
          <p class="mt-4">Selecione um setor na árvore para visualizar a Análise SWOT.</p>
        </div>

        <div v-else>
          <!-- Skeleton enquanto os dados do setor carregam -->
          <template v-if="loading">
            <v-row v-for="n in 2" :key="'skeleton-row-' + n">
              <v-col cols="12" md="6" v-for="m in 2" :key="'skeleton-col-' + n + '-' + m">
                <v-card class="swot-card">
                  <v-skeleton-loader type="heading, table-row@3"></v-skeleton-loader>
                </v-card>
              </v-col>
            </v-row>
          </template>

          <template v-else>
          <!-- Linha Superior: Forças e Oportunidades -->
          <v-row>
            <v-col cols="12" md="6">
              <v-card class="swot-card" :class="getCardClass('strengths')">
                <v-card-title class="green--text text--darken-2">
                  <v-icon left color="green darken-2">mdi-arm-flex</v-icon>Forças
                </v-card-title>
                <v-data-table
                  :headers="headers"
                  :items="swotTableData.strengths"
                  hide-default-footer
                  disable-pagination
                  dense
                  class="elevation-0"
                  :show-select="isQuadrantSelectable('strengths')"
                  v-model="selected.strengths"
                  item-key="id"
                >
                  <template v-slot:item.displayDescription="{ item }">
                    <div class="d-flex align-center">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on" class="d-inline-block text-truncate" style="max-width: 260px;">{{ item.displayDescription }}</span>
                        </template>
                        <span>{{ item.displayDescription }}</span>
                      </v-tooltip>
                      <FactorInitiativesBadge :initiatives="initiativesOf(item.id)" :loading="initiativesLoading" @open="openFactorInitiatives(item)" />
                    </div>
                  </template>
                  <template v-slot:item.perspective="{ item }">
                    <div class="d-flex justify-center">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on" :color="perspectiveColor(item.perspectiveId)" small>{{ perspectiveIcon(item.perspectiveId) }}</v-icon>
                        </template>
                        <span>{{ perspectiveName(item.perspectiveId) }}</span>
                      </v-tooltip>
                    </div>
                  </template>
                  <template v-slot:item.weight="{ item }">
                    {{ item.weight.toFixed(2) }}
                  </template>
                  <template v-slot:item.total="{ item }">
                    <strong>{{ item.total.toFixed(2) }}</strong>
                  </template>
                  <template v-slot:body.append>
                    <tr class="font-weight-bold green lighten-5">
                      <td :colspan="showCrossButtons ? 3 : 2">Resultado final</td>
                      <td class="text-right">{{ totals.strengths.weight.toFixed(2) }}</td>
                      <td></td>
                      <td class="text-right">{{ totals.strengths.total.toFixed(2) }}</td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card class="swot-card" :class="getCardClass('opportunities')">
                <v-card-title class="blue--text text--darken-2">
                  <v-icon left color="blue darken-2">mdi-star</v-icon>Oportunidades
                </v-card-title>
                <v-data-table
                  :headers="headers"
                  :items="swotTableData.opportunities"
                  hide-default-footer
                  disable-pagination
                  dense
                  class="elevation-0"
                  :show-select="isQuadrantSelectable('opportunities')"
                  v-model="selected.opportunities"
                  item-key="id"
                >
                  <template v-slot:item.displayDescription="{ item }">
                    <div class="d-flex align-center">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on" class="d-inline-block text-truncate" style="max-width: 260px;">{{ item.displayDescription }}</span>
                        </template>
                        <span>{{ item.displayDescription }}</span>
                      </v-tooltip>
                      <FactorInitiativesBadge :initiatives="initiativesOf(item.id)" :loading="initiativesLoading" @open="openFactorInitiatives(item)" />
                    </div>
                  </template>
                  <template v-slot:item.perspective="{ item }">
                    <div class="d-flex justify-center">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on" :color="perspectiveColor(item.perspectiveId)" small>{{ perspectiveIcon(item.perspectiveId) }}</v-icon>
                        </template>
                        <span>{{ perspectiveName(item.perspectiveId) }}</span>
                      </v-tooltip>
                    </div>
                  </template>
                  <template v-slot:item.weight="{ item }">
                    {{ item.weight.toFixed(2) }}
                  </template>
                  <template v-slot:item.total="{ item }">
                    <strong>{{ item.total.toFixed(2) }}</strong>
                  </template>
                  <template v-slot:body.append>
                    <tr class="font-weight-bold blue lighten-5">
                      <td :colspan="showCrossButtons ? 3 : 2">Resultado final</td>
                      <td class="text-right">{{ totals.opportunities.weight.toFixed(2) }}</td>
                      <td></td>
                      <td class="text-right">{{ totals.opportunities.total.toFixed(2) }}</td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
          </v-row>

          <!-- Linha Inferior: Fraquezas e Ameaças -->
          <v-row>
            <v-col cols="12" md="6">
              <v-card class="swot-card" :class="getCardClass('weaknesses')">
                <v-card-title class="orange--text text--darken-2">
                  <v-icon left color="orange darken-2">mdi-arrow-down-bold-circle</v-icon>Fraquezas
                </v-card-title>
                <v-data-table
                  :headers="headers"
                  :items="swotTableData.weaknesses"
                  hide-default-footer
                  disable-pagination
                  dense
                  class="elevation-0"
                  :show-select="isQuadrantSelectable('weaknesses')"
                  v-model="selected.weaknesses"
                  item-key="id"
                >
                  <template v-slot:item.displayDescription="{ item }">
                    <div class="d-flex align-center">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on" class="d-inline-block text-truncate" style="max-width: 260px;">{{ item.displayDescription }}</span>
                        </template>
                        <span>{{ item.displayDescription }}</span>
                      </v-tooltip>
                      <FactorInitiativesBadge :initiatives="initiativesOf(item.id)" :loading="initiativesLoading" @open="openFactorInitiatives(item)" />
                    </div>
                  </template>
                  <template v-slot:item.perspective="{ item }">
                    <div class="d-flex justify-center">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on" :color="perspectiveColor(item.perspectiveId)" small>{{ perspectiveIcon(item.perspectiveId) }}</v-icon>
                        </template>
                        <span>{{ perspectiveName(item.perspectiveId) }}</span>
                      </v-tooltip>
                    </div>
                  </template>
                  <template v-slot:item.weight="{ item }">
                    {{ item.weight.toFixed(2) }}
                  </template>
                  <template v-slot:item.total="{ item }">
                    <strong>{{ item.total.toFixed(2) }}</strong>
                  </template>
                  <template v-slot:body.append>
                    <tr class="font-weight-bold orange lighten-5">
                      <td :colspan="showCrossButtons ? 3 : 2">Resultado final</td>
                      <td class="text-right">{{ totals.weaknesses.weight.toFixed(2) }}</td>
                      <td></td>
                      <td class="text-right">{{ totals.weaknesses.total.toFixed(2) }}</td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card class="swot-card" :class="getCardClass('threats')">
                <v-card-title class="red--text text--darken-2">
                  <v-icon left color="red darken-2">mdi-alert</v-icon>Ameaças
                </v-card-title>
                <v-data-table
                  :headers="headers"
                  :items="swotTableData.threats"
                  hide-default-footer
                  disable-pagination
                  dense
                  class="elevation-0"
                  :show-select="isQuadrantSelectable('threats')"
                  v-model="selected.threats"
                  item-key="id"
                >
                  <template v-slot:item.displayDescription="{ item }">
                    <div class="d-flex align-center">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on" class="d-inline-block text-truncate" style="max-width: 260px;">{{ item.displayDescription }}</span>
                        </template>
                        <span>{{ item.displayDescription }}</span>
                      </v-tooltip>
                      <FactorInitiativesBadge :initiatives="initiativesOf(item.id)" :loading="initiativesLoading" @open="openFactorInitiatives(item)" />
                    </div>
                  </template>
                  <template v-slot:item.perspective="{ item }">
                    <div class="d-flex justify-center">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on" :color="perspectiveColor(item.perspectiveId)" small>{{ perspectiveIcon(item.perspectiveId) }}</v-icon>
                        </template>
                        <span>{{ perspectiveName(item.perspectiveId) }}</span>
                      </v-tooltip>
                    </div>
                  </template>
                  <template v-slot:item.weight="{ item }">
                    {{ item.weight.toFixed(2) }}
                  </template>
                  <template v-slot:item.total="{ item }">
                    <strong>{{ item.total.toFixed(2) }}</strong>
                  </template>
                  <template v-slot:body.append>
                    <tr class="font-weight-bold red lighten-5">
                      <td :colspan="showCrossButtons ? 3 : 2">Resultado final</td>
                      <td class="text-right">{{ totals.threats.weight.toFixed(2) }}</td>
                      <td></td>
                      <td class="text-right">{{ totals.threats.total.toFixed(2) }}</td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
          </v-row>

          <!-- Posição Estratégica -->
          <v-row class="mt-4">
            <v-col cols="12" md="6">
                <v-card>
                    <v-card-text class="text-center">
                        <div class="text-subtitle-1">Posição Estratégica - Fatores Internos</div>
                        <p class="text-h4 font-weight-bold my-2" :class="positionColor(internalPosition) + '--text'">
                          <v-icon :color="positionColor(internalPosition)" large class="mr-1">{{ positionIcon(internalPosition) }}</v-icon>
                          {{ internalPosition.toFixed(2) }}
                        </p>
                        <div class="grey--text">(forças - fraquezas)</div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" md="6">
                 <v-card>
                    <v-card-text class="text-center">
                        <div class="text-subtitle-1">Posição Estratégica - Fatores Externos</div>
                        <p class="text-h4 font-weight-bold my-2" :class="positionColor(externalPosition) + '--text'">
                          <v-icon :color="positionColor(externalPosition)" large class="mr-1">{{ positionIcon(externalPosition) }}</v-icon>
                          {{ externalPosition.toFixed(2) }}
                        </p>
                        <div class="grey--text">(oportunidades - ameaças)</div>
                    </v-card-text>
                </v-card>
            </v-col>
          </v-row>
          </template>
        </div>
      </v-col>
    </v-row>

    <!-- Painel das iniciativas vinculadas ao fator -->
    <FactorInitiativesDrawer
      v-model="factorDrawer.open"
      :factor="factorDrawer.factor"
      :quadrant="factorDrawer.quadrant"
      :initiatives="factorDrawer.factor ? initiativesOf(factorDrawer.factor.id) : []"
      :loading="initiativesLoading"
      :error="initiativesError"
      :objectives="objectivesById"
      :objectives-loading="objectivesLoading"
      :keep-open="openInitiativeForm"
      @retry="loadInitiatives"
      @edit="editInitiative"
    />

    <!-- Dialog de Nova Iniciativa / edição a partir do painel -->
    <v-dialog v-model="openInitiativeForm" persistent max-width="900px" scrollable>
      <ActionForm
        v-if="openInitiativeForm"
        :sector="sector"
        :action-data="editingInitiative"
        :pre-selected-factors="editingInitiative ? null : selected"
        :available-factors="swotTableData"
        @close="closeInitiativeForm"
        @save="handleInitiativeSave"
        @delete="handleDeleteInitiative"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import SectorTreeList from '../../../components/app/operative/SectorTreeList.vue';
import TitleBar from '../../../components/app/TitleBar.vue';
import ActionForm from '../../../components/app/swot/InitiativeForm.vue';
import FactorInitiativesBadge from '../../../components/app/swot/FactorInitiativesBadge.vue';
import FactorInitiativesDrawer from '../../../components/app/swot/FactorInitiativesDrawer.vue';
import swotService from '@/services/swotService';
import translateService from '@/helper/translateService.js';

// Mesma referência para fatores sem iniciativa: evita re-render das linhas a cada atualização
const NO_INITIATIVES = Object.freeze([]);

export default {
  name: 'SwotAnalysis',
  components: {
    TitleBar,
    SectorTreeList,
    ActionForm,
    FactorInitiativesBadge,
    FactorInitiativesDrawer
  },
  data() {
    return {
      sector: { id: null, name: null },
      showSidebar: false,
      loading: false,
      showCrossButtons: false,
      openInitiativeForm: false,
      // Iniciativas do setor/ciclo: uma única busca alimenta contagem, prévia e painel
      initiatives: [],
      initiativesLoading: false,
      initiativesError: false,
      editingInitiative: null,
      factorDrawer: { open: false, factor: null, quadrant: null },
      // Objetivos do ciclo, carregados só na primeira abertura do painel
      objectivesById: {},
      objectivesLoading: false,
      objectivesStageId: null,
      selected: {
        strengths: [],
        weaknesses: [],
        opportunities: [],
        threats: [],
      },
      headers: [
        { text: 'Descrição', value: 'displayDescription', sortable: true },
        { text: 'Perspectiva', value: 'perspective', align: 'center', sortable: true },
        { text: 'Peso', value: 'weight', align: 'right', sortable: true },
        { text: 'Class.', value: 'rating', align: 'right', sortable: true },
        { text: 'Total', value: 'total', align: 'right', sortable: true },
      ],
      swot: {
        strengths: [],
        weaknesses: [],
        opportunities: [],
        threats: [],
      },
    };
  },
  computed: {
    hasSelection() {
      return (
        this.selected.strengths.length > 0 ||
        this.selected.weaknesses.length > 0 ||
        this.selected.opportunities.length > 0 ||
        this.selected.threats.length > 0
      );
    },
    swotTableData() {
      const process = (items) => items.map(i => ({
        ...i,
        perspective: this.perspectiveName(i.perspectiveId),
        total: i.weight * i.rating,
        displayDescription: `${i.code || i.id} - ${i.description}`
      }));
      return {
        strengths: process(this.swot.strengths),
        weaknesses: process(this.swot.weaknesses),
        opportunities: process(this.swot.opportunities),
        threats: process(this.swot.threats),
      };
    },
    totals() {
      const calculate = (items) => {
        return items.reduce((acc, item) => {
          acc.weight += item.weight;
          acc.total += item.weight * item.rating;
          return acc;
        }, { weight: 0, total: 0 });
      };
      return {
        strengths: calculate(this.swot.strengths),
        weaknesses: calculate(this.swot.weaknesses),
        opportunities: calculate(this.swot.opportunities),
        threats: calculate(this.swot.threats),
      };
    },
    // factorId -> iniciativas distintas vinculadas (uma iniciativa aparece em cada fator a que está ligada)
    initiativesByFactor() {
      const map = {};
      this.initiatives.forEach(initiative => {
        (initiative.confirmedFactors || []).forEach(cf => {
          if (cf.id == null) return;
          const list = map[cf.id] || (map[cf.id] = []);
          if (!list.some(i => i.id === initiative.id)) list.push(initiative);
        });
      });
      return map;
    },
    internalPosition() {
      return this.totals.strengths.total - this.totals.weaknesses.total;
    },
    externalPosition() {
      return this.totals.opportunities.total - this.totals.threats.total;
    }
  },
  mounted() {
    if (this.$store.state.current.sector) {
      this.sector.id = this.$store.state.current.sector.id;
      this.sector.name = this.$store.state.current.sector.name;
      this.loadSwotData();
    }
  },
  methods: {
    toggleCrossButtons() {
      this.showCrossButtons = !this.showCrossButtons;
      if (!this.showCrossButtons) {
        this.selected = { strengths: [], weaknesses: [], opportunities: [], threats: [] };
      }
    },
    isQuadrantSelectable(type) {
      return this.showCrossButtons;
    },
    getCardClass(type) {
      const classes = ['swot-card--' + type];
      if (this.showCrossButtons) classes.push('blue lighten-5');
      return classes;
    },
    positionColor(value) {
      if (value > 0) return 'success';
      if (value < 0) return 'error';
      return 'grey';
    },
    positionIcon(value) {
      if (value > 0) return 'mdi-arrow-up-bold';
      if (value < 0) return 'mdi-arrow-down-bold';
      return 'mdi-minus';
    },
    perspectiveName(perspectiveId) {
      const p = (this.$store.state.current.perspectives || []).find(x => x.id === perspectiveId);
      return p ? translateService.getName(p.names) : '';
    },
    perspectiveIcon(perspectiveId) {
      const p = (this.$store.state.current.perspectives || []).find(x => x.id === perspectiveId);
      return p && p.icon ? p.icon : 'mdi-archive';
    },
    perspectiveColor(perspectiveId) {
      const p = (this.$store.state.current.perspectives || []).find(x => x.id === perspectiveId);
      return p && p.color ? p.color : 'grey';
    },
    createInitiative() {
      this.editingInitiative = null;
      this.openInitiativeForm = true;
    },
    closeInitiativeForm() {
      this.openInitiativeForm = false;
      this.editingInitiative = null;
    },
    handleInitiativeSave(formData) {
      const stageId = this.$store.state.current.stage && this.$store.state.current.stage.id;
      const payload = { ...formData, sectorId: this.sector.id, stageId };
      const request = payload.id
        ? swotService.updateInitiative(payload)
        : swotService.saveInitiative(payload);
      request
        .then(() => {
          this.$toastr.s(payload.id ? this.$t('swot.initiativeUpdated') : 'Iniciativa criada com sucesso!');
          // Atualiza contagens e painel com os vínculos gravados
          this.loadInitiatives();
        })
        .catch(() => { this.$toastr.e('Erro ao salvar iniciativa.'); })
        .finally(() => {
          this.closeInitiativeForm();
          this.showCrossButtons = false;
          this.selected = { strengths: [], weaknesses: [], opportunities: [], threats: [] };
        });
    },
    // Exclusão pelo formulário, mesmo fluxo da Gestão de Iniciativas
    handleDeleteInitiative(id) {
      if (!confirm('Tem certeza que deseja excluir esta iniciativa?')) return;
      swotService.deleteInitiative(id)
        .then(() => {
          this.$toastr && this.$toastr.s('Iniciativa excluída.');
          this.loadInitiatives();
        })
        .catch(() => { this.$toastr && this.$toastr.e('Erro ao excluir iniciativa.'); })
        .finally(() => { this.closeInitiativeForm(); });
    },
    initiativesOf(factorId) {
      return this.initiativesByFactor[factorId] || NO_INITIATIVES;
    },
    openFactorInitiatives(factor) {
      const quadrant = Object.keys(this.swotTableData)
        .find(key => this.swotTableData[key].some(f => f.id === factor.id));
      this.factorDrawer = { open: true, factor, quadrant };
      this.loadObjectives();
    },
    editInitiative(initiative) {
      this.editingInitiative = initiative;
      this.openInitiativeForm = true;
    },
    setSector(sector) {
      this.factorDrawer.open = false;
      if (!sector) {
        this.sector.id = null;
        this.sector.name = null;
        this.showSidebar = true;
        this.initiatives = [];
        return;
      }
      this.sector.id = sector.id;
      this.sector.name = sector.name;
      this.loadSwotData();
    },
    loadSwotData() {
      if (!this.sector.id) return;
      const stageId = this.$store.state.current.stage && this.$store.state.current.stage.id;
      if (!stageId) return;
      this.loading = true;
      swotService.getMatrix(this.sector.id, stageId)
        .then(response => { this.swot = response.data; })
        .catch(() => { this.$toastr && this.$toastr.e('Erro ao carregar a matriz SWOT.'); })
        .finally(() => { this.loading = false; });
      // Em paralelo: a matriz não espera pelas iniciativas
      this.loadInitiatives();
    },
    loadInitiatives() {
      const sectorId = this.sector.id;
      const stageId = this.$store.state.current.stage && this.$store.state.current.stage.id;
      if (!sectorId || !stageId) return;
      this.initiativesLoading = true;
      this.initiativesError = false;
      swotService.getInitiatives(sectorId, stageId)
        .then(response => {
          // Resposta de um setor que já foi trocado: descarta
          if (this.sector.id !== sectorId) return;
          this.initiatives = response.data || [];
        })
        .catch(() => {
          if (this.sector.id !== sectorId) return;
          this.initiatives = [];
          this.initiativesError = true;
          this.$toastr && this.$toastr.e(this.$t('swot.loadInitiativesError'));
        })
        .finally(() => {
          if (this.sector.id === sectorId) this.initiativesLoading = false;
        });
    },
    loadObjectives() {
      const stageId = this.$store.state.current.stage && this.$store.state.current.stage.id;
      if (!stageId || this.objectivesStageId === stageId || this.objectivesLoading) return;
      this.objectivesLoading = true;
      // Objetivos de todos os setores do ciclo, como no formulário da iniciativa
      swotService.getAllObjectives(stageId)
        .then(response => {
          const byId = {};
          (response.data || []).forEach(o => { byId[o.id] = o; });
          this.objectivesById = byId;
          this.objectivesStageId = stageId;
        })
        .catch(() => { this.objectivesById = {}; })
        .finally(() => { this.objectivesLoading = false; });
    },
  },
};
</script>

<style scoped>
.swot-card {
  border: 1px solid #e0e0e0;
  border-left-width: 4px;
  height: 100%;
}
.swot-card--strengths {
  border-left-color: #2e7d32;
}
.swot-card--opportunities {
  border-left-color: #1565c0;
}
.swot-card--weaknesses {
  border-left-color: #ef6c00;
}
.swot-card--threats {
  border-left-color: #c62828;
}
.swot-card ::v-deep tbody tr:nth-of-type(even):not(.font-weight-bold) {
  background-color: #fafafa;
}
.swot-card ::v-deep tbody tr:not(.font-weight-bold):hover {
  background-color: #f0f0f0 !important;
}
.sector-name-btn {
  cursor: pointer;
  color: var(--v-primary-base, #1976d2);
  border-bottom: 1px dashed currentColor;
  transition: opacity 0.15s ease;
}
.sector-name-btn:hover {
  opacity: 0.75;
}
tfoot {
    background-color: #f5f5f5;
}
/* Centralizar o título na TitleBar forçando via CSS - Apenas na barra principal */
.main-title-bar ::v-deep .v-toolbar__content {
  position: relative;
}

.main-title-bar ::v-deep .v-toolbar__title {
  width: 100%;
  text-align: center;
  position: absolute;
  left: 0;
  pointer-events: none; /* Garante que o título não bloqueie cliques em botões laterais */
  font-size: 2.55rem;
}
</style>
